import { useEffect, useState } from "react";
import {
  cacheKey,
  clearDashboardCache,
  flushDeferredUserWrites,
  readCache,
  setPersistAllowed,
  sweepStaleVersions,
  writeCache,
} from "../lib/dashboardCache";
import { isAppShell } from "../lib/apiError";

// Flat key->bool map keyed by FeatureFlag.kt enum values (snake_case).
// New flags require zero frontend type changes — just read the key.
export type FeatureFlags = Record<string, boolean>;

// Known flag keys, kept as a const for callsites that want autocomplete /
// rename safety. Keep in sync with FeatureFlag.kt; unknown keys are still
// valid at runtime — flagEnabled() returns false for missing keys.
export const FEATURE_FLAGS = {
  aiConfig: "ai_config",
  aiManagedEnabled: "ai_managed_enabled",
  launchNewEnabled: "launch_new_enabled",
  integrationsConnectEnabled: "integrations_connect_enabled",
  instanceSharing: "instance_sharing",
  billingStripe: "billing_stripe",
  billingCustomer: "billing_customer",
  userProfile: "user_profile",
  toolUsageDashboard: "tool_usage_dashboard",
} as const;

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

export const flagEnabled = (
  features: FeatureFlags | undefined,
  key: FeatureFlagKey,
): boolean => Boolean(features?.[key]);

export type ImpersonationState = {
  active: true;
  originalAdminEmail: string;
  viewingAs: string;
};

// Caller's role on a SPECIFIC shared instance. OWNER = the instance owner;
// MANAGER/MEMBER = collaborators the owner shared the instance with.
export type MemberRole = "OWNER" | "MANAGER" | "MEMBER";

export type AuthUser = {
  displayName?: string | null;
  avatarUrl?: string | null;
  email: string;
  subject: string;
  isAdmin: boolean;
  userBudget: number;
  // Purchased tier from the user's invite row; null when unknown. Read
  // via `?? null` where it matters - cached /api/me payloads written
  // before this field existed lack it entirely.
  tier: string | null;
  // Catalog slugs an admin pinned to this user's invite (IP-588); the
  // onboarding wizard turns them into "connect these next" deep links.
  // Read via `?? []` - cached /api/me payloads written before this field
  // existed lack it entirely.
  requestedIntegrations: ReadonlyArray<string>;
  bookOnboardingCallUrl: string;
  purchaseUrl: string;
  features: FeatureFlags;
  impersonation: ImpersonationState | null;
};

// "unavailable" is NOT a flavour of "unauthenticated". /api/me returning a
// 5xx, or the fetch rejecting outright, says nothing about the session - it
// says ztc-core is down. Collapsing the two (which this hook used to do) sent
// the browser to /oauth2/authorization/cognito, an ALB-backed path, which
// during an outage is exactly the raw 503 page a customer should never see.
// It also cleared the dashboard cache on the way out, so the session came
// back cold once the platform recovered.
export type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "unavailable";

export type UseAuthResult = {
  status: AuthStatus;
  user: AuthUser | null;
  isRevalidating: boolean;
  // The HTTP status behind an "unavailable" result, so the outage screen
  // can name it. null when the fetch never got a response at all.
  unavailableStatus: number | null;
};

// The one rule that matters here, pulled out so it can be tested without a
// renderer: a 5xx is the platform being down, anything else non-200 is the
// session being gone. Conflating them is what sent customers to the raw ALB
// 503 during an outage.
export const classifyMeStatus = (httpStatus: number): AuthStatus =>
  httpStatus >= 500 ? "unavailable" : httpStatus === 200 ? "authenticated" : "unauthenticated";

sweepStaleVersions();

const ME_KEY = cacheKey(null, "me");

type MeResult = { status: AuthStatus; user: AuthUser | null; unavailableStatus?: number };

// useAuth is mounted by many components at once; share one in-flight
// /api/me request across them. Cleared after settling so later mounts
// (SPA navigation) still revalidate.
let inflightMe: Promise<MeResult> | null = null;
const authListeners = new Set<(result: MeResult) => void>();

// Drain older reads before fetching after a mutation: their stale response
// must not win over a profile save in another mounted useAuth consumer.
export const refreshAuth = async (): Promise<void> => {
  if (inflightMe !== null) await inflightMe;
  await fetchMe();
};

const fetchMe = (): Promise<MeResult> => {
  if (inflightMe !== null) return inflightMe;
  inflightMe = fetch("/api/me", { credentials: "include" })
    .then(async (res): Promise<MeResult> => {
      // Keep the cache: the session is probably still good, the platform
      // just isn't answering. Wiping it here would make an outage cost the
      // customer their warm dashboard on top of the downtime.
      if (classifyMeStatus(res.status) === "unavailable") {
        setPersistAllowed(false);
        return { status: "unavailable", user: null, unavailableStatus: res.status };
      }
      // A 200 carrying HTML is CloudFront's 403/404 -> /index.html rewrite,
      // not a session (see isAppShell). Treat it as the 403 it really was:
      // without this the res.json() below throws and lands in the catch, which
      // would report an outage to somebody who just needs to sign in again.
      if (res.status !== 200 || isAppShell(res)) {
        clearDashboardCache();
        setPersistAllowed(false);
        return { status: "unauthenticated", user: null };
      }
      const body = (await res.json()) as AuthUser;
      // Never persist while impersonating: the admin's cache must not hold
      // the impersonated user's data, nor be scoped to their email.
      if (body.impersonation === null) {
        const cached = readCache<AuthUser>(ME_KEY);
        if (cached !== null && cached.email !== body.email) clearDashboardCache();
        setPersistAllowed(true);
        writeCache(ME_KEY, body);
        flushDeferredUserWrites();
      } else {
        setPersistAllowed(false);
      }
      return { status: "authenticated", user: body };
    })
    // A rejected fetch is a network/DNS/TLS failure or an offline browser,
    // never a 401. Same reasoning as the 5xx branch above.
    .catch((): MeResult => ({ status: "unavailable", user: null }))
    .then((result) => {
      authListeners.forEach((listener) => listener(result));
      return result;
    })
    .finally(() => {
      inflightMe = null;
    });
  return inflightMe;
};

export const useAuth = (): UseAuthResult => {
  const [cached] = useState<AuthUser | null>(() => readCache<AuthUser>(ME_KEY));
  const [status, setStatus] = useState<AuthStatus>(cached !== null ? "authenticated" : "loading");
  const [user, setUser] = useState<AuthUser | null>(cached);
  const [isRevalidating, setIsRevalidating] = useState<boolean>(true);
  const [unavailableStatus, setUnavailableStatus] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const receive = (result: MeResult): void => {
      if (cancelled) return;
      setUser(result.user);
      setStatus(result.status);
      setUnavailableStatus(result.unavailableStatus ?? null);
      setIsRevalidating(false);
    };
    authListeners.add(receive);
    void fetchMe().then(receive);
    return () => {
      cancelled = true;
      authListeners.delete(receive);
    };
  }, []);

  return { status, user, isRevalidating, unavailableStatus };
};
